import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

/**
 * Tiny in-memory cache so swiping prev/next doesn't refetch the entire archive
 * for every keypress. Lives for the tab session.
 */
const _cache = {
  dossier: null,
  ai_dossier: null,
  connection: null,
  loaded_at: null,
};
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function _isFresh() {
  return _cache.loaded_at && Date.now() - _cache.loaded_at < CACHE_TTL_MS;
}

async function _ensureLoaded(type) {
  if (_isFresh() && _cache[type]) return _cache[type];
  // Fetch all three lanes once, sort A-Z by display title for predictable swipe order
  try {
    const [dossiers, dives, conns] = await Promise.all([
      api.get("/dossiers", { params: { limit: 5000 }, timeout: 8000 }).then((r) => r.data).catch(() => []),
      api.get("/deep-dive/published", { params: { limit: 500 }, timeout: 8000 }).then((r) => r.data).catch(() => []),
      api.get("/connect-dots/published", { params: { limit: 500 }, timeout: 8000 }).then((r) => r.data).catch(() => []),
    ]);
    _cache.dossier = (dossiers || [])
      .map((d) => ({ id: d.slug, title: d.title || "" }))
      .sort((a, b) => a.title.localeCompare(b.title));
    _cache.ai_dossier = (dives || [])
      .map((d) => ({ id: d.id, title: d.topic || "" }))
      .sort((a, b) => a.title.localeCompare(b.title));
    _cache.connection = (conns || [])
      .map((c) => ({ id: c.id, title: c.title || "" }))
      .sort((a, b) => a.title.localeCompare(b.title));
    _cache.loaded_at = Date.now();
  } catch {
    /* swallow — neighbors will simply be null */
  }
  return _cache[type];
}

/**
 * useArchiveNeighbors(type, currentId)
 * Returns { prev, next, list, index } for the given file in its lane.
 * - type: "dossier" | "ai_dossier" | "connection"
 * - currentId: slug for dossier, id for ai_dossier/connection
 * Each neighbor is { id, title } or null when at the edge.
 */
export default function useArchiveNeighbors(type, currentId) {
  const [list, setList] = useState(_cache[type] || []);

  useEffect(() => {
    let cancelled = false;
    _ensureLoaded(type).then((arr) => {
      if (!cancelled && arr) setList(arr);
    });
    return () => {
      cancelled = true;
    };
  }, [type]);

  return useMemo(() => {
    if (!list || list.length === 0 || !currentId) {
      return { prev: null, next: null, list: list || [], index: -1 };
    }
    const idx = list.findIndex((x) => x.id === currentId);
    if (idx === -1) {
      return { prev: null, next: null, list, index: -1 };
    }
    return {
      prev: idx > 0 ? list[idx - 1] : null,
      next: idx < list.length - 1 ? list[idx + 1] : null,
      list,
      index: idx,
    };
  }, [list, currentId]);
}
